import React, { useState } from 'react';

import { HELP_LINKS } from '../../../common/helpLinks.js';
import { fetchStarterTemplates, importStarterTemplate } from '../lib/api';

function dispatchEntityAction(detail) {
  document.dispatchEvent(new CustomEvent('ui:entity-action', {
    detail,
  }));
}

function dispatchToast({
  variant = 'secondary',
  title = 'Timeline',
  message = '',
  delay = 5000,
}) {
  document.dispatchEvent(new CustomEvent('ui:toast', {
    detail: {
      variant,
      title,
      message,
      delay,
    },
  }));
}

export default function TimelineEmptyOnboarding({
  selectedDate,
  onImported,
  onImportingChange,
}) {
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState('');
  const [importingKey, setImportingKey] = useState('');
  const [expanded, setExpanded] = useState(false);

  async function loadTemplates() {
    if (loading || templatesLoaded) {
      return;
    }

    setLoading(true);
    setTemplateLoadError('');
    try {
      const data = await fetchStarterTemplates();
      setTemplates(Array.isArray(data) ? data : []);
      setTemplatesLoaded(true);
    } catch (error) {
      setTemplateLoadError('Starter-Vorlagen konnten nicht geladen werden.');
      dispatchToast({
        variant: 'danger',
        title: 'Vorlagen',
        message: `Starter-Vorlagen konnten nicht geladen werden: ${error?.message ?? String(error)}`,
        delay: 7000,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleTemplateToggle() {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    if (nextExpanded && !templatesLoaded && !templateLoadError) {
      void loadTemplates();
    }
  }

  async function handleImport(template) {
    const templateKey = String(template?.key ?? '').trim();
    if (!templateKey || importingKey) {
      return;
    }

    setImportingKey(templateKey);
    onImportingChange?.(true);
    try {
      const result = await importStarterTemplate(templateKey, {
        target_date: selectedDate,
      });
      const counts = result?.created ?? {};
      dispatchToast({
        variant: 'success',
        title: 'Starter-Vorlage',
        message: `${template?.name ?? 'Vorlage'} importiert: ${Number(counts.workflows ?? 0)} Workflows und ${Number(counts.tasks ?? 0)} Tasks erstellt.`,
        delay: 7000,
      });
      await onImported?.(result);
    } catch (error) {
      dispatchToast({
        variant: 'danger',
        title: 'Starter-Vorlage',
        message: `Import fehlgeschlagen: ${error?.message ?? String(error)}`,
        delay: 8000,
      });
    } finally {
      setImportingKey('');
      onImportingChange?.(false);
    }
  }

  function openDayWorkflowCreate() {
    dispatchEntityAction({
      source: 'timeline-empty-onboarding',
      entity: 'day-workflows',
      action: 'create',
      prefill: {
        date: selectedDate,
        time_mode: 'start',
        time_value: '09:00',
      },
    });
  }

  function openRegularWorkflowCreate() {
    dispatchEntityAction({
      source: 'timeline-empty-onboarding',
      entity: 'regular-workflows',
      action: 'create',
      prefill: {
        start_date: selectedDate,
        time_mode: 'start',
        time_value: '09:00',
      },
    });
  }

  return (
    <section
      className="timeline-empty-onboarding"
      aria-label="Leere Timeline starten"
      data-testid="timeline-empty-onboarding"
    >
      <div className="timeline-empty-onboarding-copy">
        <div className="timeline-empty-onboarding-title">Noch nichts geplant.</div>
        <p>Starte direkt mit einem Eintrag auf der Timeline.</p>
      </div>

      <div className="timeline-empty-onboarding-actions">
        <button
          type="button"
          className="btn timeline-empty-direct-action"
          onClick={openDayWorkflowCreate}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          <span>Einmaligen Ablauf planen</span>
        </button>
        <button
          type="button"
          className="btn timeline-empty-direct-action"
          onClick={openRegularWorkflowCreate}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          <span>Wiederkehrende Routine planen</span>
        </button>

        <div className="timeline-empty-choice-separator" aria-hidden="true">
          <span>oder</span>
        </div>

        <button
          type="button"
          className="btn results-table-empty-state-action results-table-empty-state-action-primary timeline-empty-primary-action"
          onClick={handleTemplateToggle}
          aria-expanded={expanded ? 'true' : 'false'}
          data-testid="timeline-empty-template-toggle"
        >
          <i className="bi bi-stars" aria-hidden="true" />
          <span>Mit Vorlage starten</span>
        </button>
      </div>

      {expanded ? (
        <div
          className="timeline-empty-template-list"
          data-testid="timeline-empty-template-list"
          aria-live="polite"
        >
          {loading ? (
            <div
              className="timeline-empty-template-card is-muted"
              data-testid="timeline-empty-template-loading"
            >
              Vorlagen werden geladen...
            </div>
          ) : null}

          {!loading && templateLoadError ? (
            <div
              className="timeline-empty-template-card timeline-empty-template-status"
              data-testid="timeline-empty-template-error"
            >
              <div>
                <h3>Vorlagen sind gerade nicht erreichbar.</h3>
                <span className="timeline-empty-template-status-copy">
                  Plane direkt einen Ablauf oder versuche es erneut.
                </span>
              </div>
              <button
                type="button"
                className="btn results-table-empty-state-action results-table-empty-state-action-secondary timeline-empty-template-retry"
                onClick={() => void loadTemplates()}
              >
                Erneut laden
              </button>
            </div>
          ) : null}

          {!loading && templatesLoaded && templates.length === 0 ? (
            <div
              className="timeline-empty-template-card timeline-empty-template-status is-muted"
              data-testid="timeline-empty-template-empty"
            >
              <div>
                <h3>Aktuell sind keine Vorlagen verfügbar.</h3>
                <span className="timeline-empty-template-status-copy">
                  Plane deinen ersten Ablauf mit den Aktionen oben.
                </span>
              </div>
            </div>
          ) : null}

          {!loading && templatesLoaded && templates.map((template) => {
            const counts = template?.counts ?? {};
            const isImporting = importingKey === String(template?.key ?? '');
            return (
              <article className="timeline-empty-template-card" key={String(template?.key ?? template?.name)}>
                <div>
                  <h3>{template?.name ?? 'Starter-Vorlage'}</h3>
                  <span className="timeline-empty-template-meta">
                    {Number(counts.workflows ?? 0)} Workflows · {Number(counts.tasks ?? 0)} Tasks · {Number(counts.regular_workflows ?? 0)} Routinen
                  </span>
                </div>
                <button
                  type="button"
                  className="btn results-table-empty-state-action results-table-empty-state-action-primary timeline-empty-template-import"
                  onClick={() => handleImport(template)}
                  disabled={Boolean(importingKey)}
                  data-testid={`timeline-empty-import-${String(template?.key ?? '')}`}
                >
                  {isImporting ? 'Importiere...' : 'Importieren'}
                </button>
              </article>
            );
          })}
        </div>
      ) : null}

      <a
        className="timeline-empty-help-link"
        href={HELP_LINKS.gettingStarted}
        data-testid="timeline-empty-help"
      >
        <i className="bi bi-book" aria-hidden="true" />
        <span>Erste Schritte ansehen</span>
      </a>
    </section>
  );
}
